import React, { useEffect } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Alert, Button, Snackbar } from "@mui/material";
import { ThemeProvider } from "@mui/material";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./routes/ProtectedRoute";
import { useAuth } from "./hooks/useAuth";
import { SnackbarProvider } from "notistack";
import Admin from "./pages/admin";
import Student from "./pages/student";
import Teacher from "./pages/teacher";
import SignIn from "./pages/sign-in/SignIn";
import StudentSignUp from "./pages/sign-in/StudentSignUp";
import EmailConfirmation from "./pages/sign-in/EmailConfirmation";
import { AuthRoute } from "./routes/AuthRoute";
import NotFound from "./components/errors/NotFound";
import { darkTheme } from "./theme/darkTheme";
import { lightTheme } from "./theme/lightTheme";
import { themes } from "./theme/roleThemes";

import PasswordReset from "./pages/sign-in/PasswordReset";
import { LoadingProvider } from "./context/LoadingContext";
import { StudentGuard } from "./routes/StudentGuard";
import AccountInactivePage from "./pages/student/AccountInactivePage";
import StudentOnboardingPage from "./pages/student/StudentOnboardingPage";
import ContactTeacherPage from "./pages/student/ContactTeacherPage";

// =============================================================================
// 1. ADD THIS IMPORT
// =============================================================================
import TestEnvironmentBanner from "./components/TestEnvironmentBanner";
import { useVersionCheck } from "./hooks/useVersionCheck";

const getThemeForPath = (pathname: string) => {
  if (pathname.startsWith("/auth")) {
    return darkTheme;
  }
  if (pathname.startsWith("/student")) {
    return themes.student.light;
  }
  if (pathname.startsWith("/teacher")) {
    return themes.teacher.light;
  }
  if (pathname.startsWith("/admin")) {
    return themes.admin.light;
  }
  return themes.student.light;
};

const App: React.FC = () => {
  console.log(process.env.REACT_APP_BACKEND_API_URL, "BACKEND URL");
  const location = useLocation();
  const { updateAvailable, checkForUpdate } = useVersionCheck();

  useEffect(() => {
    checkForUpdate();
  }, [location.pathname, checkForUpdate]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForUpdate();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [checkForUpdate]);

  const currentTheme = getThemeForPath(location.pathname);

  return (
    <>
      <TestEnvironmentBanner />

      <Snackbar
        open={updateAvailable}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
          }
        >
          A new version is available.
        </Alert>
      </Snackbar>

      <ThemeProvider theme={currentTheme}>
        <SnackbarProvider maxSnack={3}>
          <LoadingProvider>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </LoadingProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </>
  );
};

const AppContent: React.FC = () => {
  return (
    <Routes>
      <Route path="/auth" element={<AuthRoute />}>
        <Route index element={<Navigate to="sign-in" replace />} />
        <Route path="sign-in" element={<SignIn />} />
        <Route path="sign-up" element={<StudentSignUp />} />
        <Route path="email-confirmation" element={<EmailConfirmation />} />
        <Route path="password-reset" element={<PasswordReset />} />
        <Route path="*" element={<Navigate to="/auth/sign-in" replace />} />
      </Route>
      <Route path="/" element={<Navigate to="/auth/sign-in" />} />

      {/* Student Error Pages */}
      <Route
        path="/student/account-inactive"
        element={
          <ProtectedRoute>
            <AccountInactivePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/waiting-assignment"
        element={
          <ProtectedRoute>
            <StudentOnboardingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/student/contact-teacher"
        element={
          <ProtectedRoute>
            <ContactTeacherPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="student/*"
        element={
          <ProtectedRoute>
            <StudentGuard>
              <Student />
            </StudentGuard>
          </ProtectedRoute>
        }
      />

      <Route
        path="teacher/*"
        element={
          <ProtectedRoute>
            <Teacher />
          </ProtectedRoute>
        }
      />

      <Route
        path="admin/*"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />

      {/* 404 Fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default App;
